import { useEffect, useState } from "react";
import EmergencyRequestForm from "./EmergencyRequestForm";
import emergencyHeroPhoto from "../../assets/design/emergency-care-photo.jpg";
import "./EmergencyRequestWidget.css";

/**
 * Public, pre-registration "Need Care Urgently?" lead-capture banner for the
 * homepage. A SEPARATE system from the existing logged-in-only instant-match
 * EmergencyCareModal (see EMERGENCY_BOOKING_MODULE_CHECKLIST.md §1) — this
 * one is a human-mediated callback, not an instant auto-match, and supports
 * Care Attendant / Nurse / Physiotherapist (not just Care Attendant).
 */

function SirenIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <path
        d="M12 3v2M5 7l1.4 1.4M19 7l-1.4 1.4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M6 15a6 6 0 1 1 12 0v2H6v-2Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function CareAttendantIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <circle cx="12" cy="7.2" r="3.2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M9.5 13.8v1.4M14.5 13.8v1.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function NurseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <circle cx="12" cy="9.5" r="3.3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4.5 20c0-3.6 3.3-6 7.5-6s7.5 2.4 7.5 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <rect x="9.3" y="2.4" width="5.4" height="4.2" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <path d="M12 3.4v2.2M10.9 4.5h2.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function PhysioIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <circle cx="17.3" cy="5.2" r="1.7" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M14.6 9.2 17 7.4l2.4 2.1-1.6 3.3M3.5 19.5l3.9-1.1 2.3-4.1 3.1 1 2.6-1.3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9.7 14.3 11.6 17l2.4.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <circle cx="12" cy="13" r="7.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 9.2V13l2.6 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M9.6 2.6h4.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <circle cx="9" cy="8.3" r="2.7" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="16.3" cy="9.3" r="2.1" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3.6 19c0-2.9 2.4-5 5.4-5s5.4 2.1 5.4 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M15.4 14.3c2.3.3 4 2.1 4 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path
        d="M12 3.2 19 6v5.4c0 4.4-2.9 7.6-7 8.6-4.1-1-7-4.2-7-8.6V6l7-2.8Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="m8.8 12.2 2.2 2.2 4.2-4.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <rect x="3.6" y="5" width="16.8" height="15" rx="2.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.6 9.6h16.8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 3v3.6M16 3v3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M7.6 13h2.4M7.6 16.2h2.4M11.4 13h2.4M11.4 16.2h2.4M15.2 13h1.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const SERVICES = [
  { key: "attendant", label: "Care Attendant", Icon: CareAttendantIcon, tone: "magenta" },
  { key: "nurse", label: "Nurse", Icon: NurseIcon, tone: "blue" },
  { key: "physio", label: "Physiotherapist", Icon: PhysioIcon, tone: "purple" },
];

const STATS = [
  { key: "sla", Icon: ClockIcon, lines: ["Quick Response", "Within 15 Minutes"] },
  { key: "cm", Icon: PeopleIcon, lines: ["Care Manager", "Assistance"] },
  { key: "verified", Icon: ShieldCheckIcon, lines: ["Verified & Trained", "Caregivers (NCID)"] },
  { key: "shifts", Icon: CalendarIcon, lines: ["Service for", "Hourly, 12-Hr & 24-Hr"] },
];

export default function EmergencyRequestWidget() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(event) {
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="emreq-widget" aria-label="Emergency care request">
      <div className="emreq-widget__content">
        <div className="emreq-widget__copy">
          <span className="emreq-widget__badge">
            <span className="emreq-widget__badge-icon">
              <SirenIcon />
            </span>
            Emergency Care Request
          </span>

          <h2 className="emreq-widget__title">
            Need Care <span>Urgently?</span>
          </h2>

          <p className="emreq-widget__desc">
            Skip searching caregivers. Tell us your requirement and our Care Team will arrange the
            most suitable caregiver for you.
          </p>

          <div className="emreq-widget__services">
            {SERVICES.map(({ key, label, Icon, tone }) => (
              <div className="emreq-widget__service" key={key}>
                <span className={`emreq-widget__service-icon emreq-widget__service-icon--${tone}`}>
                  <Icon />
                </span>
                <span>{label}</span>
              </div>
            ))}
          </div>

          <button type="button" className="emreq-widget__cta" onClick={() => setOpen(true)}>
            <BoltIcon />
            Request Emergency Care
            <ArrowRightIcon />
          </button>

          <div className="emreq-widget__stats">
            {STATS.map(({ key, Icon, lines }) => (
              <div className="emreq-widget__stat" key={key}>
                <span className="emreq-widget__stat-icon">
                  <Icon />
                </span>
                <span className="emreq-widget__stat-text">
                  {lines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="emreq-widget__media" aria-hidden="true">
          <span className="emreq-widget__media-glow" />
          <img
            className="emreq-widget__media-photo"
            src={emergencyHeroPhoto}
            alt=""
            loading="lazy"
          />
        </div>
      </div>

      {open ? (
        <div className="emreq-widget__backdrop" role="presentation" onClick={() => setOpen(false)}>
          <section
            className="emreq-widget__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="emreq-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="emreq-widget__dialog-header">
              <div>
                <h3 id="emreq-dialog-title">Need Care Urgently?</h3>
                <p>We&apos;ll contact you within 15 minutes to arrange the most suitable caregiver.</p>
              </div>
              <button
                type="button"
                className="emreq-widget__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <EmergencyRequestForm requireOtp source="HOMEPAGE" onCancel={() => setOpen(false)} />
          </section>
        </div>
      ) : null}
    </div>
  );
}
